<script>
  import { page } from "$app/stores"
  import "../app.postcss"
  import { navigating } from "$app/stores"
  import { browser, dev } from "$app/environment" // Import dev flag
  import { onMount } from "svelte"
  import { fade, slide } from "svelte/transition"
  import { afterNavigate } from "$app/navigation"
  import { supabase, getSupabaseClient } from "$lib/supabase.ts"
  import { invalidate } from "$app/navigation"
  import Navbar from "$lib/components/layout/Navbar.svelte"
  import Footer from "$lib/components/layout/Footer.svelte"

  // Dark mode
  const dark = true

  // Route protection logic can be added here if needed
  $: isAdminRoute = $page.url.pathname.startsWith("/account")

  // Check if we're on the home page to decide whether to show the animation
  $: isHomePage = $page.url.pathname === "/"

  // After navigation, scroll to top
  afterNavigate(({ to }) => {
    const hash = to?.url.hash
    if (!hash) {
      window.scrollTo({ top: 0 })
    }
  })

  onMount(() => {
    if (browser) {
      // Force dark theme
      document.documentElement.style.backgroundColor = "#0F172A"
      document.body.style.backgroundColor = "#0F172A"
      document.documentElement.setAttribute("data-theme", "neural")

      // Only log in development mode
      if (dev) {
        console.log("Environment check in +layout.svelte:")
        console.log(
          "window.env keys:",
          window.env ? Object.keys(window.env) : "not available",
        )
        console.log(
          "global supabase client initialized:",
          !!window.__SUPABASE_CLIENT,
        )
      }

      // Try to verify Supabase connectivity
      try {
        const client = getSupabaseClient()
        if (dev) {
          console.log(
            "Supabase client initialized successfully in +layout.svelte",
          )
        }

        // Test connection by getting session
        client.auth
          .getSession()
          .then((result) => {
            if (dev) {
              console.log(
                "Supabase auth session check result:",
                result.data ? "Success" : "No session",
              )
            }
          })
          .catch((error) => {
            console.error("Failed to get Supabase session")
          })
      } catch (error) {
        console.error("Failed to initialize Supabase client in +layout.svelte")
      }
    }

    // Auth listener to refresh the session when it changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      invalidate("supabase:auth")
    })

    return () => {
      subscription.unsubscribe()
    }
  })

  // Get current URL path
  $: path = $page.url.pathname

  // Determine if current page has its own layout
  $: hasCustomLayout = ["/blog", "/blog/"].some((prefix) =>
    path.startsWith(prefix),
  )

  // Only use MainLayout if the page doesn't have its own layout
  $: useMainLayout = !hasCustomLayout
</script>

<svelte:head>
  <title>Hollas.net</title>
  <meta
    name="description"
    content="Hollas AB specializes in digital transformation, AI implementation, and innovative technology solutions for businesses looking to grow and evolve."
  />
  <!-- Set viewport meta tag to ensure proper scaling -->
  <meta
    name="viewport"
    content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
  />
  <link rel="icon" href="/favicon.ico" />

  <!-- Open Graph / Social Media -->
  <meta property="og:type" content="website" />
  <meta property="og:url" content={$page.url.href} />
  <meta
    property="og:title"
    content="Hollas AB - Digital Transformation & AI Solutions"
  />
  <meta
    property="og:description"
    content="Hollas AB specializes in digital transformation, AI implementation, and innovative technology solutions for businesses looking to grow and evolve."
  />
  <meta property="og:image" content="/images/og-image.jpg" />
</svelte:head>

<!-- Main app container with dark theme -->
<div class="app-container bg-[#0F172A] text-white">
  <!-- Loading bar -->
  {#if $navigating}
    <div
      class="fixed w-full top-0 right-0 left-0 h-1 z-50"
      in:slide={{ duration: 300, axis: "x" }}
      out:fade={{ duration: 200 }}
    >
      <div class="loading loading-dots loading-lg text-accent"></div>
    </div>
  {/if}

  <!-- For non-blog pages, show navbar and footer -->
  {#if !hasCustomLayout}
    <!-- Fixed navbar -->
    <Navbar {dark} />

    <!-- Main content with padding for fixed navbar -->
    <main class="relative z-10 min-h-screen pt-16">
      <slot />
    </main>

    <!-- Footer -->
    <Footer />
  {:else}
    <!-- Blog pages have their own layout with navbar/footer -->
    <slot />
  {/if}
</div>

<style>
  /* Ensure the background is dark and viewport covers full height */
  :global(html),
  :global(body) {
    background-color: #0f172a !important;
    color: white !important;
    margin: 0;
    padding: 0;
    width: 100%;
    height: 100%;
    min-height: 100vh;
    overflow-x: hidden; /* Prevent horizontal scroll */
  }

  .app-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 100vh;
    position: relative;
    overflow-x: hidden; /* Prevent horizontal scroll */
  }
</style>
